import { cn } from "@/lib/utils";
import { navigationItems } from "./navigationItems";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { ChevronDown, ChevronRight, ChevronLeft, ChevronUp, Star } from "lucide-react";
import InicioBadgeIcon from "@/components/icons/InicioBadgeIcon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NavigationItem } from "./navigationItems";
import { useFavorites } from "@/hooks/useFavorites";
import { FavoritesSection } from "@/components/navigation/FavoritesSection";
import { FavoriteToggle } from "@/components/navigation/FavoriteToggle";
import type { FavoriteMenuItem } from "@/types/favorites";

interface DesktopSidebarProps {
  isSidebarExpanded: boolean;
  toggleSidebar: () => void;
  navItems: typeof navigationItems;
  profile?: {
    full_name?: string;
    avatar_url?: string;
  };
  user?: {
    email?: string;
  };
}

// A11y helper to create stable ids from titles
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export function DesktopSidebar({
  isSidebarExpanded,
  toggleSidebar,
  navItems,
  profile,
  user
}: DesktopSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // Favorites hook
  const {
    favorites,
    isLoading: favoritesLoading,
    isFavorite,
    toggleFavorite,
  } = useFavorites();

  // Helper function to convert NavigationItem to FavoriteMenuItem
  const toFavoriteMenuItem = (item: NavigationItem, category?: string): FavoriteMenuItem => {
    // Extract icon name from Lucide component
    // Lucide components have displayName like "Home" or name property
    const iconName = item.icon?.displayName || item.icon?.name || 'Circle';

    return {
      id: item.path || item.title,
      label: item.title,
      icon: iconName,
      path: item.path || '#',
      category,
      timestamp: Date.now(),
    };
  };

  // Estado para controlar qual item do menu inicial está selecionado
  // Inicia com "Início" selecionado
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>("Início");

  // Estado para controlar expansão dos subitens no painel escuro
  const [expandedSubItems, setExpandedSubItems] = useState<{ [key: string]: boolean }>({
    "Cadastros": true,
    "Configurações": false,
    "CADASTROS": true, // Submenu do menu Serviços
    "CONFIGURAÇÕES": true, // Submenu do menu Serviços
  });

  // Estado fixo (pinned) é fornecido pelo MainLayout
  const isPinned = isSidebarExpanded;

  // Estado para controlar hover no menu inicial
  const [isHovered, setIsHovered] = useState(false);

  // Estado para controlar hover específico no item "Início"
  const [isInicioHovered, setIsInicioHovered] = useState(false);

  // Expansão especial do Início (painel com mesma cor da col1 mostrando itens de primeiro nível com nomes)
  const [showInicioExpandedPanel, setShowInicioExpandedPanel] = useState(false);

  // Ref para throttle de hover events
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();

  // Determinar se o sidebar deve estar expandido (fixo OU hover)
  const shouldBeExpanded = isPinned || isHovered;

  // Comunicar estado de hover para o MainLayout APENAS quando não estiver fixo
  // Isso evita loops desnecessários quando o sidebar está fixo
  useEffect(() => {
    // Só dispara evento se não estiver fixo para evitar loops
    if (!isPinned) {
      const event = new CustomEvent('sidebarHoverChange', {
        detail: { isHoverExpanded: isHovered }
      });
      window.dispatchEvent(event);
    }
  }, [isHovered, isPinned]);

  // Helper function to check if a route is active
  const isRouteActive = (path: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  // Filtrar itens para o menu inicial (todos exceto "Início")
  const initialMenuItems = useMemo(() => {
    return navItems.filter(item => item.title !== "Início");
  }, [navItems]);

  // Itens da coluna 1: somente itens de primeiro nível, sem duplicados
  const col1Items = useMemo(() => {
    const order = [
      "Início",
      "Dashboard",
      "Todas as Transações",
      "Fluxo de Caixa",
      "Inadimplentes",
      "Competência",
      "Relatórios",
      "Serviços",
      "Cadastros",
      "Conciliação Bancária",
      "Configurações",
    ];
    const map = new Map(navItems.map(i => [i.title, i] as const));
    return order
      .map(title => map.get(title))
      .filter(Boolean) as typeof navItems;
  }, [navItems]);

  // Detectar qual item deve estar selecionado baseado na rota atual
  useEffect(() => {
    let foundMatch = false;

    // Função recursiva para verificar subitens aninhados
    const checkSubItems = (subItems: NavigationItem[]): boolean => {
      for (const subItem of subItems) {
        // Verificar se o subitem tem a rota ativa
        if (subItem.path && isRouteActive(subItem.path)) {
          return true;
        }
        // Verificar subitens aninhados recursivamente
        if (subItem.subItems && subItem.subItems.length > 0) {
          if (checkSubItems(subItem.subItems)) {
            return true;
          }
        }
      }
      return false;
    };

    // Verificar se algum item corresponde à rota atual
    for (const item of navItems) {
      // Verificar se o próprio item corresponde à rota (prioridade para itens com path direto)
      if (item.path && isRouteActive(item.path)) {
        setSelectedMenuItem(item.title);
        foundMatch = true;
        return;
      }

      // Verificar subitens
      if (item.subItems && item.subItems.length > 0) {
        if (checkSubItems(item.subItems)) {
          // Se encontrou um subitem que corresponde à rota, selecionar o item pai
          setSelectedMenuItem(item.title);
          foundMatch = true;
          return;
        }
      }
    }

    // Se nenhum item corresponde à rota, garantir que "Início" está selecionado
    if (!foundMatch) {
      setSelectedMenuItem("Início");
    }
  }, [location.pathname, navItems]);

  // Obter itens para mostrar no painel escuro baseado no item selecionado
  const getDarkPanelItems = (): NavigationItem[] => {
    if (selectedMenuItem === "Favoritos") {
      // Quando "Favoritos" está selecionado, converter favorites para NavigationItems
      return favorites.map(fav => {
        // Encontrar o item original nos navItems para pegar o ícone correto
        const findItemByPath = (items: NavigationItem[]): NavigationItem | undefined => {
          for (const item of items) {
            if (item.path === fav.path) return item;
            if (item.subItems) {
              const found = findItemByPath(item.subItems);
              if (found) return found;
            }
          }
          return undefined;
        };

        const originalItem = findItemByPath(navItems);

        return {
          title: fav.label,
          icon: originalItem?.icon || (() => null),
          path: fav.path,
        };
      });
    }

    if (selectedMenuItem === "Início") {
      // Quando "Início" está selecionado, mostrar todos os itens principais
      return initialMenuItems;
    }

    // Encontrar o item selecionado
    const selectedItem = navItems.find(item => item.title === selectedMenuItem);

    if (selectedItem?.subItems && selectedItem.subItems.length > 0) {
      // Se o item tem subItems, retornar os subItems
      return selectedItem.subItems;
    }

    // Fallback: se o item não tem subItems, manter uma experiência consistente mostrando o menu inicial
    return initialMenuItems;
  };

  const darkPanelItems = getDarkPanelItems();
  const selectedItem = navItems.find(item => item.title === selectedMenuItem);

  // Toggle expansão de subitens no painel escuro
  const toggleSubItemExpansion = (title: string) => {
    setExpandedSubItems(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Determinar se deve mostrar o painel escuro
  // Não deve aparecer quando o painel especial do Início estiver aberto
  const shouldShowDarkPanel = shouldBeExpanded && !showInicioExpandedPanel;

  // Controlar montagem do painel escuro para permitir animações de entrada/saída
  const [isDarkPanelMounted, setIsDarkPanelMounted] = useState(shouldShowDarkPanel);
  const darkPanelHideTimeoutRef = useRef<NodeJS.Timeout>();

  // Montar/desmontar o painel escuro com atraso para permitir animação de saída
  useEffect(() => {
    if (shouldShowDarkPanel) {
      if (darkPanelHideTimeoutRef.current) {
        clearTimeout(darkPanelHideTimeoutRef.current);
      }
      setIsDarkPanelMounted(true);
      return;
    }

    // Aguarda a animação antes de desmontar
    darkPanelHideTimeoutRef.current = setTimeout(() => {
      setIsDarkPanelMounted(false);
    }, 180);
  }, [shouldShowDarkPanel]);

  // Função para alternar o estado fixo
  const handleToggleSidebar = () => {
    console.log('[DesktopSidebar] Toggle button clicked, current isPinned:', isPinned);

    // Chamar a função do MainLayout para alternar o estado
    toggleSidebar();

    // Se estava fixo e estamos desfixando, remover hover
    if (isPinned) {
      setIsHovered(false);
    }
  };

  // O localStorage é gerenciado pelo MainLayout
  // Este componente apenas reflete o estado recebido via props

  // Cleanup de timeouts quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (darkPanelHideTimeoutRef.current) {
        clearTimeout(darkPanelHideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <aside
        className={cn(
          "hidden lg:flex fixed left-0 z-30 h-[calc(100vh-4rem)]",
          // Transição mais suave para largura
          "transition-[width] duration-200 ease-[cubic-bezier(.4,0,.2,1)]",
          // Adicionar will-change para otimizar animações
          "will-change-[width]",
          shouldBeExpanded ? "w-[264px]" : "w-16"
        )}
      >
      {/* Container Unificado para Hover Management */}
      <div
        className="relative flex h-full"
        tabIndex={0}
        onFocus={() => setIsHovered(true)}
        onBlur={(e) => {
          const next = (e.relatedTarget as Node) || null;
          if (!e.currentTarget.contains(next) && !isPinned) {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = setTimeout(() => {
              setIsHovered(false);
              setIsInicioHovered(false);
            }, 250);
          }
        }}
        onMouseEnter={() => {
          // Limpar timeout existente
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          // Se o painel especial do Início estiver aberto, manter expanded
          if (showInicioExpandedPanel) {
            setIsHovered(true);
            return;
          }
          // Setar hover imediatamente para todo o sidebar
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          // Usar throttle para evitar flickering
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          hoverTimeoutRef.current = setTimeout(() => {
            if (!isPinned) {
              setIsHovered(false);
              setIsInicioHovered(false); // Resetar também o hover do Início
              setShowInicioExpandedPanel(false); // Fechar expansão especial
            }
          }, 300); // Ajustado para 300ms para reduzir latência mantendo suavidade
        }}
      >
        {/* Menu Inicial - Painel Esquerdo (Azul Claro) */}
        {!showInicioExpandedPanel && (
          <div
            className="flex flex-col h-full w-16"
            style={{ backgroundColor: 'hsl(var(--sidebar-initial-background))' }}
          >
        <nav className="flex-1 py-4 space-y-1" role="navigation" aria-label="Menu principal">
          {col1Items
            .map((item) => {
            const isSelected = selectedMenuItem === item.title;
            const isInicio = item.title === "Início";

            return (
              <button
                key={item.title}
                onClick={() => {
                  // Se o item tem path direto e não é "Início", navegar diretamente
                  if (item.title !== "Início" && item.path && (!item.subItems || item.subItems.length === 0)) {
                    navigate(item.path);
                    return;
                  }
                  // Se não tem subItems e não tem path (não deveria acontecer), voltar para "Início"
                  if (item.title !== "Início" && (!item.subItems || item.subItems.length === 0) && !item.path) {
                    setSelectedMenuItem("Início");
                    return;
                  }
                  // Caso contrário, selecionar o item (mostrar subItems no painel direito)
                  setSelectedMenuItem(item.title);
                }}
                onMouseEnter={() => {
                  if (isInicio) {
                    setIsInicioHovered(true);
                  }
                }}
                className={cn(
                  "w-full flex items-center justify-center py-3 relative cursor-pointer transition-transform duration-150 active:scale-95",
                  "transition-colors duration-200 ease-out",
                  "transition-transform duration-150",
                  isSelected
                    ? shouldBeExpanded
                      ? "bg-[hsl(var(--sidebar-initial-active-bg))] text-sidebar-foreground"
                      : "bg-[hsl(var(--background))] text-sidebar-foreground"
                    : "text-sidebar-foreground hover:bg-[hsl(var(--sidebar-initial-hover))]"
                )}
                title={item.title}
              >
                {item.title === "Início" ? (
                  <InicioBadgeIcon
                    className={cn(
                      "h-5 w-5 flex-shrink-0 transition-transform duration-200 hover:scale-105",
                      isSelected && !shouldBeExpanded
                        ? "opacity-80"
                        : "opacity-100"
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowInicioExpandedPanel(prev => !prev);
                      setIsHovered(true);
                    }}
                    role="button"
                    aria-label="Abrir menu inicial expandido"
                    tabIndex={0}
                  />
                ) : (
                  <item.icon className={cn(
                    "h-5 w-5 flex-shrink-0",
                    "transition-transform duration-200 hover:scale-105",
                    isSelected && !shouldBeExpanded
                      ? "text-foreground"
                      : "text-sidebar-foreground"
                  )} />
                )}
                {isInicio && favorites.length > 0 && (
                  <span className="absolute top-1 right-1 bg-accent text-sidebar-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {favorites.length}
                  </span>
                )}
                    {isSelected && shouldBeExpanded && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-1 bg-[hsl(var(--sidebar-initial-active-bg))]"
                    style={{
                      backgroundColor: 'hsl(var(--sidebar-background))',
                      transform: 'translateX(100%)'
                    }}
                  />
                )}
              </button>
            );
          })}

          {/* Botão Favoritos - Sempre visível */}
          <button
            onClick={() => setSelectedMenuItem("Favoritos")}
            className={cn(
              "w-full flex items-center justify-center py-3 relative cursor-pointer transition-transform duration-150 active:scale-95",
              "transition-colors duration-200 ease-out",
              "transition-transform duration-150",
              selectedMenuItem === "Favoritos"
                ? shouldBeExpanded
                  ? "bg-[hsl(var(--sidebar-initial-active-bg))] text-sidebar-foreground"
                  : "bg-[hsl(var(--background))] text-sidebar-foreground"
                : "text-sidebar-foreground hover:bg-[hsl(var(--sidebar-initial-hover))]"
            )}
            title="Favoritos"
          >
            <Star
              className={cn(
                "h-5 w-5 flex-shrink-0",
                "transition-transform duration-200 hover:scale-105",
                selectedMenuItem === "Favoritos" && !shouldBeExpanded
                  ? "text-foreground"
                  : "text-sidebar-foreground"
              )}
              fill={selectedMenuItem === "Favoritos" ? "currentColor" : "none"}
            />
            {/* Badge com contagem de favoritos */}
            {favorites.length > 0 && (
              <span className="absolute top-1 right-1 bg-accent text-sidebar-foreground text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {favorites.length}
              </span>
            )}
            {selectedMenuItem === "Favoritos" && shouldBeExpanded && (
              <div
                className="absolute right-0 top-0 bottom-0 w-1 bg-[hsl(var(--sidebar-initial-active-bg))]"
                style={{
                  backgroundColor: 'hsl(var(--sidebar-background))',
                  transform: 'translateX(100%)'
                }}
              />
            )}
          </button>
        </nav>

        {/* User Section no Menu Inicial */}
        {(profile || user) && (
          <div className="p-2 border-t border-sidebar-border">
            <div className="flex items-center justify-center">
              <Avatar className="w-8 h-8">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-sidebar-accent/20 text-sidebar-foreground text-sm font-semibold">
                  {profile?.full_name?.[0] || user?.email?.[0] || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        )}
      </div>
       )}

      {/* Área de Buffer Invisível entre colunas */}
        {shouldShowDarkPanel && (
          <div
            className="absolute left-16 top-0 bottom-0 w-[2px] z-[55] bg-transparent"
            style={{ pointerEvents: 'auto' }}
          />
        )}

        {/* Painel Especial do Início (aparência da col1, itens de primeiro nível com nomes) */}
        {showInicioExpandedPanel && (
          <div
            className={cn(
              "flex flex-col h-full w-[264px] overflow-visible",
              "transition-all duration-200 ease-[cubic-bezier(.4,0,.2,1)] will-change-[transform,opacity] motion-reduce:transition-none motion-reduce:transform-none",
              isHovered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2 pointer-events-none"
            )}
            role="region"
            aria-label="Menu inicial expandido"
            style={{ backgroundColor: 'hsl(var(--sidebar-initial-background))' }}
            onMouseLeave={() => {
              // Fecha ao sair com mouse
              setShowInicioExpandedPanel(false);
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" role="navigation" aria-label="Menu principal (expandido)">
              {col1Items.map((col1Item) => (
                <button
                  key={col1Item.title}
                  onClick={() => {
                    // Navega/seleciona e fecha a expansão especial
                    if (col1Item.path) navigate(col1Item.path);
                    else setSelectedMenuItem(col1Item.title);
                    setShowInicioExpandedPanel(false);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-3 text-[15px] font-semibold rounded-md cursor-pointer",
                    "transition-colors duration-200 ease-out",
                    "transition-transform duration-150",
                    "text-sidebar-foreground hover:bg-sidebar-accent/20"
                  )}
                >
                  <col1Item.icon className="h-[18px] w-[18px] flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                  <span className="flex-1 text-left truncate leading-tight">{col1Item.title}</span>
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Painel Escuro - Painel Direito */}
      {isDarkPanelMounted && (
        <div
          className={cn(
            "flex flex-col h-full w-[200px] border-l border-sidebar-border overflow-visible",
            "transition-all duration-200 ease-[cubic-bezier(.4,0,.2,1)] will-change-[transform,opacity] motion-reduce:transition-none motion-reduce:transform-none",
            shouldShowDarkPanel
              ? "opacity-100 translate-x-0"
              : "opacity-0 -translate-x-2 pointer-events-none"
          )}
          role={shouldShowDarkPanel ? "region" : undefined}
          aria-label={shouldShowDarkPanel ? "Submenu" : undefined}
          {...(!shouldShowDarkPanel && { inert: "" })}
          style={{ backgroundColor: 'hsl(var(--sidebar-background))' }}
        >
          {/* Header do Painel Escuro */}
          {selectedMenuItem !== "Início" && (selectedItem || selectedMenuItem === "Favoritos") && (
            <div className="px-4 py-3 border-b border-sidebar-border">
              <div className="flex items-center gap-2 text-sidebar-foreground">
                <button
                  onClick={() => setSelectedMenuItem("Início")}
                  className="hover:bg-sidebar-hover rounded p-1 cursor-pointer transition-colors duration-200 ease-out transition-transform duration-150 active:scale-[0.98]"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="font-medium">
                  {selectedMenuItem === "Favoritos" ? "Favoritos" : selectedItem?.title}
                </span>
              </div>
            </div>
          )}

          {/* Badges Expandidos quando hover em "Início" */}
          {isInicioHovered && favorites.length > 0 && (
            <div
              className="px-3 py-2 border-b border-sidebar-border"
              onMouseEnter={() => setIsInicioHovered(true)}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 bg-accent rounded-full flex items-center justify-center">
                  <Star className="h-2 w-2 text-sidebar-foreground" fill="currentColor" />
                </div>
                <span className="text-sm font-semibold text-sidebar-foreground">Favoritos</span>
                <span className="ml-auto text-xs text-sidebar-foreground/60">{favorites.length}</span>
              </div>
              <nav className="space-y-1">
                {favorites.map((favorite) => {
                  const IconComponent = (() => {
                    try {
                      const icon = (require('lucide-react') as any)[favorite.icon];
                      return icon && typeof icon === 'function' ? icon : Star;
                    } catch {
                      return Star;
                    }
                  })();

                  const isActive = favorite.path ? isRouteActive(favorite.path) : false;

                  return (
                    <button
                      key={favorite.id}
                      onClick={() => {
                        navigate(favorite.path);
                        setIsInicioHovered(false); // Fechar após navegação
                      }}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer group",
                        "transition-colors duration-200 ease-out",
                        "transition-transform duration-150",
                        "hover:bg-sidebar-accent/20",
                        isActive
                          ? "bg-sidebar-accent/30 text-sidebar-foreground"
                          : "text-sidebar-foreground/80 hover:text-sidebar-foreground"
                      )}
                    >
                      <IconComponent className="h-[18px] w-[18px] flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                      <span className="text-sm text-left truncate flex-1">{favorite.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

          {/* Favorites Section - Always at top (except when Favoritos is selected or Início hover) */}
          {selectedMenuItem !== "Favoritos" && !isInicioHovered && (
            <FavoritesSection
              favorites={favorites}
              onItemClick={(item) => {
                navigate(item.path);
              }}
              isExpanded={shouldBeExpanded}
              maxVisibleItems={8}
              className="mt-2"
            />
          )}

          {/* Conteúdo do Painel Escuro */}
          <nav className="flex-1 overflow-y-auto overflow-x-visible py-4 px-2 space-y-1">
            {darkPanelItems.map((item) => {
              const isActive = item.path ? isRouteActive(item.path) : false;
              const hasSubItems = item.subItems && item.subItems.length > 0;
              const isExpanded = expandedSubItems[item.title] ?? false;
              const isDeleteAccount = item.title === "Excluir Conta";

              if (hasSubItems) {
                return (
                  <div key={item.title || item.path} className="space-y-1">
                    <div className="group relative">
                      <button
                        onClick={() => {
                          // Se estamos em "Início" e clicamos em um item com subItems, selecionar esse item no menu inicial
                          if (selectedMenuItem === "Início") {
                            // Encontrar o item correspondente no navItems
                            const navItem = navItems.find(navItem => navItem.title === item.title);
                            if (navItem) {
                              setSelectedMenuItem(navItem.title);
                            }
                          } else {
                            // Se já estamos no item selecionado, apenas expandir/colapsar
                            toggleSubItemExpansion(item.title);
                          }
                        }}
                        className={cn(
                          "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer transition-transform duration-150 active:scale-95",
                          "transition-colors duration-200 ease-out",
                          "transition-transform duration-150",
                          "text-sidebar-foreground hover:bg-sidebar-accent/20"
                        )}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                        <span className="flex-1 text-left truncate">{item.title}</span>
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                      {/* Favorite Toggle - Hidden by default, shown on hover */}
                      <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <FavoriteToggle
                          item={toFavoriteMenuItem(item, selectedItem?.title)}
                          isFavorited={isFavorite(item.path || item.title)}
                          onToggle={(favItem) => toggleFavorite(favItem)}
                          size="sm"
                        />
                      </div>
                    </div>
                    <div
                      className={cn(
                        "grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(.4,0,.2,1)]",
                        "will-change-[grid-template-rows]",
                        "motion-reduce:transition-none",
                        isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                      )}
                    >
                      <div className="overflow-hidden">
                        <div className="ml-4 space-y-1">
                          {item.subItems?.map((subItem) => {
                          const isSubActive = subItem.path ? isRouteActive(subItem.path) : false;
                          const isSubDeleteAccount = subItem.title === "Excluir Conta";
                          const hasNestedSubItems = subItem.subItems && subItem.subItems.length > 0;
                          const isNestedExpanded = expandedSubItems[subItem.title] ?? false;

                          // Se o subitem tem subitens aninhados (como CADASTROS e CONFIGURAÇÕES)
                          if (hasNestedSubItems) {
                            return (
                              <div key={subItem.title || subItem.path} className="space-y-1">
                                <div className="group relative">
                                  <button
                                                aria-expanded={isNestedExpanded}
                                                aria-controls={`submenu-${slug(subItem.title)}`}
                                                id={`toggle-${slug(subItem.title)}`}
                                                onClick={() => toggleSubItemExpansion(subItem.title)}
                                    className={cn(
                                      "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md cursor-pointer transition-transform duration-150 active:scale-95",
                                      "transition-colors duration-200 ease-out",
                                      "transition-transform duration-150",
                                      "text-sidebar-foreground hover:bg-sidebar-accent/20"
                                    )}
                                  >
                                    <subItem.icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                                    <span className="flex-1 text-left truncate">{subItem.title}</span>
                                    {isNestedExpanded ? (
                                      <ChevronDown className="h-3 w-3" />
                                    ) : (
                                      <ChevronRight className="h-3 w-3" />
                                    )}
                                  </button>
                                </div>
                                <div
                                  className={cn(
                                    "grid transition-[grid-template-rows] duration-200 ease-[cubic-bezier(.4,0,.2,1)]",
                                    "will-change-[grid-template-rows]",
                                    "motion-reduce:transition-none",
                                    isNestedExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                                  )}
                                >
                                  <div className="overflow-hidden">
                                    <div className="ml-4 space-y-1" role="group" id={`submenu-${slug(subItem.title)}`} aria-labelledby={`toggle-${slug(subItem.title)}`}>
                                      {subItem.subItems?.map((nestedSubItem) => {
                                      const isNestedActive = nestedSubItem.path ? isRouteActive(nestedSubItem.path) : false;
                                      return (
                                        <div key={nestedSubItem.path} className="group relative">
                                          <Link
                                            to={nestedSubItem.path || "#"}
                                            className={cn(
                                              "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer relative transition-transform duration-150 active:scale-95",
                                              "transition-colors duration-200 ease-out",
                                              "transition-transform duration-150",
                                              isNestedActive
                                                ? "bg-background text-foreground -ml-[10px] mr-[-8px] pr-4 rounded-none z-10"
                                                : "text-sidebar-foreground/80 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground rounded-md"
                                            )}
                                          >
                                            <nestedSubItem.icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                                            <span className="truncate">{nestedSubItem.title}</span>
                                          </Link>
                                          {/* Favorite Toggle - Hidden by default, shown on hover */}
                                          <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                            <FavoriteToggle
                                              item={toFavoriteMenuItem(nestedSubItem, subItem.title)}
                                              isFavorited={isFavorite(nestedSubItem.path || nestedSubItem.title)}
                                              onToggle={(favItem) => toggleFavorite(favItem)}
                                              size="sm"
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          // Subitem normal sem subitens aninhados
                          return (
                            <div key={subItem.path} className="group relative">
                              <Link
                                to={subItem.path || "#"}
                                className={cn(
                                  "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer relative transition-transform duration-150 active:scale-95",
                                  "transition-colors duration-200 ease-out",
                                  "transition-transform duration-150",
                                  isSubActive
                                    ? "bg-background text-foreground -ml-[10px] mr-[-8px] pr-4 rounded-none z-10"
                                    : isSubDeleteAccount
                                    ? "text-red-400 hover:bg-red-500/15 hover:text-red-300 rounded-md"
                                    : "text-sidebar-foreground/80 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground rounded-md"
                                )}
                              >
                                <subItem.icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                                <span className="truncate">{subItem.title}</span>
                              </Link>
                              {/* Favorite Toggle - Hidden by default, shown on hover */}
                              <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                <FavoriteToggle
                                  item={toFavoriteMenuItem(subItem, item.title)}
                                  isFavorited={isFavorite(subItem.path || subItem.title)}
                                  onToggle={(favItem) => toggleFavorite(favItem)}
                                  size="sm"
                                />
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={item.path || item.title} className="group relative">
                  <Link
                    to={item.path || "#"}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer relative transition-transform duration-150 active:scale-95",
                      "transition-colors duration-200 ease-out",
                      "transition-transform duration-150",
                      isActive
                        ? "bg-background text-foreground -ml-[10px] mr-[-8px] pr-4 rounded-none z-10"
                        : isDeleteAccount
                        ? "text-red-400 hover:bg-red-500/15 hover:text-red-300 rounded-md"
                        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground rounded-md"
                    )}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0 transition-transform duration-200 hover:scale-105" />
                    <span className="truncate">{item.title}</span>
                  </Link>
                  {/* Favorite Toggle - Hidden by default, shown on hover */}
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <FavoriteToggle
                      item={toFavoriteMenuItem(item, selectedItem?.title)}
                      isFavorited={isFavorite(item.path || item.title)}
                      onToggle={(favItem) => toggleFavorite(favItem)}
                      size="sm"
                    />
                  </div>
                </div>
              );
            })}
          </nav>
        </div>
      )}
        </div>
      </aside>

      {/* Circular Toggle Button - Simplificado e robusto */}
      <button
        type="button"
        onClick={(e) => {
          console.log('[DesktopSidebar] Button clicked!');
          e.preventDefault();
          e.stopPropagation();
          handleToggleSidebar();
        }}
        className={cn(
          "hidden lg:flex fixed bottom-[110px] z-[70]",
          "w-9 h-9 rounded-full",
          "bg-background shadow-lg border-2 border-border",
          "items-center justify-center",
          "transition-all duration-300 ease-in-out",
          "transition-transform duration-150",
          "hover:scale-105 active:scale-[0.98]",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          // Posicionar baseado no estado efetivo (expandido ou não)
          shouldBeExpanded ? "left-[264px]" : "left-[72px]"
        )}
        aria-label={isPinned ? "Desfixar menu" : "Fixar menu"}
        title={isPinned ? "Desfixar menu" : "Fixar menu"}
        style={{ cursor: 'pointer' }}
      >
        <ChevronLeft
          className={cn(
            "w-[18px] h-[18px] text-primary transition-transform duration-300",
            // Indicar o estado fixo (pinned), não o estado efetivo
            !isPinned && "rotate-180"
          )}
        />
      </button>
    </>
  );
}
